import { useEffect, useRef, useState, useCallback, memo } from 'react';
import * as PIXI from 'pixi.js';
import { Live2DModel, config as live2dCubismConfig, MotionPriority } from 'pixi-live2d-display/cubism4';

Live2DModel.registerTicker(PIXI.Ticker);

function safeDestroyModel(model) {
  try {
    if (model && typeof model.destroy === 'function') model.destroy();
  } catch (_) {
    /* ignore */
  }
}

function safeDestroyApp(app) {
  try {
    if (app && typeof app.destroy === 'function') {
      app.destroy(true, { children: true, texture: true });
    }
  } catch (_) {
    /* ignore */
  }
}

function waitForHostLayout(host, minPx = 8, maxFrames = 90) {
  return new Promise((resolve) => {
    let n = 0;
    const step = () => {
      if (!host || n++ >= maxFrames) {
        resolve();
        return;
      }
      if (host.clientWidth >= minPx && host.clientHeight >= minPx) {
        resolve();
        return;
      }
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  });
}

function rafOnce() {
  return new Promise((r) => requestAnimationFrame(r));
}

function forceTransparentCanvas(app) {
  if (!app?.view) return;
  app.view.style.background = 'transparent';
  const r = app.renderer;
  if (!r) return;
  if (r.background) {
    if (typeof r.background.alpha === 'number') r.background.alpha = 0;
    if (typeof r.background.color !== 'undefined') {
      try {
        r.background.color = 0x000000;
      } catch (_) {
        /* ignore */
      }
    }
  }
  if (r.gl && typeof r.gl.clearColor === 'function') {
    r.gl.clearColor(0, 0, 0, 0);
  }
}

function layoutModel(
  model,
  app,
  layoutVerticalAlign = 'center',
  scaleMultiplier = 1,
  bottomShiftRatio = 0,
  layoutBottomPadding,
  horizontalRatio = 0.5
) {
  const ih = model.internalModel?.height;
  if (!ih) return;
  const pad = 0.92;
  const sy = (app.screen.height * pad) / ih;
  const s = Math.min(sy, 2.5) * scaleMultiplier;
  model.scale.set(s);
  model.x = app.screen.width * horizontalRatio;
  const margin =
    typeof layoutBottomPadding === 'number'
      ? layoutBottomPadding
      : Math.max(10, app.screen.height * 0.02);
  if (layoutVerticalAlign === 'bottom') {
    model.anchor.set(0.5, 1);
    model.y = app.screen.height - margin + app.screen.height * bottomShiftRatio;
  } else {
    model.anchor.set(0.5, 0.5);
    model.y = app.screen.height * 0.52;
  }
}

function applyLoopToMotion(motion, loop) {
  if (!motion || typeof motion.setIsLoop !== 'function') return;
  motion.setIsLoop(!!loop);
}

function applyLoopToAllCachedMotions(motionManager, loop) {
  if (!motionManager?.motionGroups) return;
  for (const g of Object.keys(motionManager.motionGroups)) {
    const arr = motionManager.motionGroups[g];
    if (!Array.isArray(arr)) continue;
    for (let i = 0; i < arr.length; i++) applyLoopToMotion(arr[i], loop);
  }
}

/**
 * @param {boolean} [suppressDefaultMotionFade=true] — only for first load from bind pose; motion→motion uses normal Cubism fade.
 */
function Live2DStage({
  modelUrl,
  motionGroup = 'idle',
  motionIndex = 0,
  className = '',
  backgroundColor = 0x141418,
  backgroundAlpha = 1,
  speedMultiplier = 1,
  layoutVerticalAlign = 'center',
  layoutScaleMultiplier = 1,
  layoutBottomShiftRatio = 0,
  layoutBottomPadding,
  layoutHorizontalRatio = 0.5,
  motionLoop = true,
  showLoadingOverlay = true,
  suppressDefaultMotionFade = true,
  /** Cubism `motionFinish` — для зацикленных клипов не вызывается. */
  onMotionFinish,
  /**
   * Смена без смены motionGroup/motionIndex (два узла графа с одним клипом) — иначе Cubism не перезапускает motion.
   */
  motionGraphNodeId,
  /**
   * Игра/граф: прервать текущий (в т.ч. зацикленный) motion и стартовать новый сразу с кроссфейдом Cubism.
   * Иначе MotionState отклоняет startMotion(NORMAL), пока loop «играет».
   */
  interruptPriorMotion = false,
  /** Приоритет Cubism для model.motion (по умолчанию NORMAL; для графа нужен FORCE). */
  motionStartPriority = MotionPriority.NORMAL,
  /** Увеличивается при повторном запуске того же клипа (например нет ребра done). */
  motionReplayNonce = 0,
}) {
  const hostRef = useRef(null);
  const appRef = useRef(null);
  const speedRef = useRef(speedMultiplier);
  const motionLoopRef = useRef(motionLoop);
  const onMotionFinishRef = useRef(onMotionFinish);
  const loadedModelUrlRef = useRef('');
  const motionManagerRef = useRef(null);
  const [loadGeneration, setLoadGeneration] = useState(0);
  const [status, setStatus] = useState(() => ({
    text: showLoadingOverlay ? 'Loading model…' : '',
    error: false,
  }));

  useEffect(() => {
    speedRef.current = speedMultiplier;
  }, [speedMultiplier]);

  useEffect(() => {
    motionLoopRef.current = motionLoop;
  }, [motionLoop]);

  useEffect(() => {
    onMotionFinishRef.current = onMotionFinish;
  }, [onMotionFinish]);

  const resizeToHost = useCallback(
    (app) => {
      const host = hostRef.current;
      if (!host || !app?.renderer) return;
      const w0 = Math.max(1, host.clientWidth);
      const h0 = Math.max(1, host.clientHeight);
      app.renderer.resize(w0, h0);
      if (app.view) {
        app.view.style.width = `${w0}px`;
        app.view.style.height = `${h0}px`;
        app.view.style.display = 'block';
      }
      const model = app.stage.children[0];
      if (model)
        layoutModel(
          model,
          app,
          layoutVerticalAlign,
          layoutScaleMultiplier,
          layoutBottomShiftRatio,
          layoutBottomPadding,
          layoutHorizontalRatio
        );
      if (backgroundAlpha === 0) forceTransparentCanvas(app);
    },
    [
      layoutVerticalAlign,
      layoutScaleMultiplier,
      layoutBottomShiftRatio,
      layoutBottomPadding,
      layoutHorizontalRatio,
      backgroundAlpha
    ]
  );

  /** Load / destroy Pixi + Cubism model when modelUrl (or canvas setup) changes — not on motion changes. */
  useEffect(() => {
    const host = hostRef.current;
    if (!host || !modelUrl) return undefined;

    if (typeof window.Live2DCubismCore === 'undefined') {
      setStatus({
        text: 'Live2D Cubism Core is not loaded (live2dcubismcore.min.js).',
        error: true,
      });
      return undefined;
    }

    let disposed = false;
    let app = null;
    let model = null;
    let ro = null;
    let tickerTick = null;
    motionManagerRef.current = null;

    const onMotionStart = (group, index) => {
      const mm = motionManagerRef.current;
      if (!mm) return;
      const motion = mm.motionGroups[group]?.[index];
      applyLoopToMotion(motion, motionLoopRef.current);
    };

    const onMotionFinishInternal = () => {
      onMotionFinishRef.current?.();
    };

    (async () => {
      let fadeRestore = null;
      try {
        if (showLoadingOverlay) {
          setStatus({ text: 'Loading model…', error: false });
        } else {
          setStatus({ text: '', error: false });
        }
        await waitForHostLayout(host);
        if (disposed) return;

        if (suppressDefaultMotionFade) {
          fadeRestore = {
            motion: live2dCubismConfig.motionFadingDuration,
            idle: live2dCubismConfig.idleMotionFadingDuration
          };
          live2dCubismConfig.motionFadingDuration = 0;
          live2dCubismConfig.idleMotionFadingDuration = 0;
        }

        app = new PIXI.Application({
          backgroundColor,
          backgroundAlpha,
          antialias: true,
          resolution: window.devicePixelRatio || 1,
          autoDensity: true,
          width: Math.max(1, host.clientWidth),
          height: Math.max(1, host.clientHeight),
          ...(backgroundAlpha === 0 ? { useContextAlpha: 'notMultiplied' } : {}),
        });
        if (disposed) {
          safeDestroyApp(app);
          app = null;
          return;
        }
        appRef.current = app;
        host.appendChild(app.view);
        app.view.style.width = `${Math.max(1, host.clientWidth)}px`;
        app.view.style.height = `${Math.max(1, host.clientHeight)}px`;
        app.view.style.display = 'block';
        if (backgroundAlpha === 0) {
          forceTransparentCanvas(app);
        }

        model = await Live2DModel.from(modelUrl, { autoInteract: false });
        if (disposed) {
          safeDestroyModel(model);
          model = null;
          if (appRef.current === app) appRef.current = null;
          safeDestroyApp(app);
          app = null;
          return;
        }
        if (!model) {
          if (appRef.current === app) appRef.current = null;
          safeDestroyApp(app);
          app = null;
          if (!disposed) {
            setStatus({ text: 'Model load returned no instance.', error: true });
          }
          return;
        }
        app.stage.addChild(model);
        model.update(0);
        resizeToHost(app);
        model.autoUpdate = false;
        tickerTick = () => {
          if (disposed || !model) return;
          const dt = PIXI.Ticker.shared.deltaMS * speedRef.current;
          model.update(dt);
        };
        PIXI.Ticker.shared.add(tickerTick);
        motionManagerRef.current = model.internalModel?.motionManager;
        const mm = motionManagerRef.current;
        if (mm?.on) {
          mm.on('motionStart', onMotionStart);
          mm.on('motionFinish', onMotionFinishInternal);
        }

        loadedModelUrlRef.current = modelUrl;
        if (!disposed) setLoadGeneration((g) => g + 1);
        if (!disposed) setStatus({ text: '', error: false });
      } catch (e) {
        console.error(e);
        if (tickerTick) {
          PIXI.Ticker.shared.remove(tickerTick);
          tickerTick = null;
        }
        safeDestroyModel(model);
        model = null;
        if (appRef.current === app) appRef.current = null;
        safeDestroyApp(app);
        app = null;
        loadedModelUrlRef.current = '';
        if (!disposed) {
          setStatus({
            text: e?.message ? String(e.message) : String(e),
            error: true,
          });
        }
      } finally {
        if (fadeRestore) {
          live2dCubismConfig.motionFadingDuration = fadeRestore.motion;
          live2dCubismConfig.idleMotionFadingDuration = fadeRestore.idle;
        }
      }
    })();

    ro = new ResizeObserver(() => {
      if (appRef.current) resizeToHost(appRef.current);
    });
    ro.observe(host);

    return () => {
      disposed = true;
      const mm = motionManagerRef.current;
      if (mm?.off) {
        try {
          mm.off('motionStart', onMotionStart);
          mm.off('motionFinish', onMotionFinishInternal);
        } catch (_) {
          /* ignore */
        }
      }
      motionManagerRef.current = null;
      loadedModelUrlRef.current = '';
      if (tickerTick) {
        PIXI.Ticker.shared.remove(tickerTick);
        tickerTick = null;
      }
      ro?.disconnect();
      ro = null;
      if (appRef.current) {
        try {
          appRef.current.destroy(true, { children: true, texture: true });
        } catch (_) {
          /* ignore */
        }
        appRef.current = null;
      }
      app = null;
    };
  }, [
    modelUrl,
    backgroundColor,
    backgroundAlpha,
    resizeToHost,
    showLoadingOverlay,
    suppressDefaultMotionFade,
  ]);

  /** Same model instance: switch motion with Cubism fade (no global zero-fade). */
  useEffect(() => {
    if (loadGeneration < 1) return undefined;
    const app = appRef.current;
    const model = app?.stage?.children?.[0];
    if (!model || loadedModelUrlRef.current !== modelUrl) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const mm = model.internalModel?.motionManager;
        if (interruptPriorMotion && mm && typeof mm.stopAllMotions === 'function') {
          mm.stopAllMotions();
        }
        const priority =
          motionStartPriority != null ? motionStartPriority : MotionPriority.NORMAL;
        const started = await model.motion(motionGroup, motionIndex, priority);
        if (cancelled) return;
        if (!started && process.env.NODE_ENV === 'development') {
          console.warn('[Live2DStage] model.motion отклонён MotionState', {
            motionGroup,
            motionIndex,
            priority,
            interruptPriorMotion
          });
        }
        if (
          motionGraphNodeId != null &&
          String(motionGraphNodeId).trim() !== '' &&
          typeof window !== 'undefined' &&
          window.localStorage?.getItem('vrDebugDialogueEmotion') === '1'
        ) {
          console.info('[DialogueEmotion] Live2D.motion applied', {
            graphNodeId: String(motionGraphNodeId),
            motionGroup,
            motionIndex,
            motionLoop,
            started
          });
        }
        if (mm) {
          if (motionLoop) {
            applyLoopToAllCachedMotions(mm, true);
            applyLoopToMotion(mm.motionGroups?.[motionGroup]?.[motionIndex], true);
          } else {
            applyLoopToMotion(mm.motionGroups?.[motionGroup]?.[motionIndex], false);
          }
        }
        model.update(0);
        resizeToHost(app);
        try {
          app.renderer?.render?.(app.stage);
        } catch (_) {
          /* ignore */
        }
        await rafOnce();
        if (!cancelled) {
          model.update(16);
          resizeToHost(app);
          try {
            app.renderer?.render?.(app.stage);
          } catch (_) {
            /* ignore */
          }
        }
      } catch (e) {
        console.error(e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    loadGeneration,
    modelUrl,
    motionGroup,
    motionIndex,
    motionLoop,
    motionGraphNodeId,
    motionReplayNonce,
    interruptPriorMotion,
    motionStartPriority,
    resizeToHost
  ]);

  useEffect(() => {
    if (appRef.current) resizeToHost(appRef.current);
  }, [
    layoutVerticalAlign,
    layoutScaleMultiplier,
    layoutBottomShiftRatio,
    layoutBottomPadding,
    layoutHorizontalRatio,
    resizeToHost
  ]);

  return (
    <div className={`relative flex min-h-0 min-w-0 flex-1 flex-col ${className}`}>
      {status.text ? (
        <div
          className={`pointer-events-none absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 rounded-lg px-5 py-3 text-sm ${
            status.error ? 'bg-black/70 text-red-300' : 'bg-black/55 text-zinc-300'
          }`}
          role="status"
        >
          {status.text}
        </div>
      ) : null}
      <div ref={hostRef} className="h-full min-h-0 w-full min-w-0 flex-1" />
    </div>
  );
}

export default memo(Live2DStage);
